import { formatDate, formatDateTime, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { CHIP_COLORS } from "@src/appV2/lib/Theme/constants";
import { type Shift } from "@src/interface";
import {
  formatDuration,
  intervalToDuration,
  isAfter,
  isBefore,
  isPast,
  isToday,
  isTomorrow,
  parseISO,
} from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { UrgencyType } from "../../Shifts/types";
import { type ShiftChip, type ShiftChipData, type ShiftChipType, ShiftChipTypes } from "./types";

const aTeamChipStyles = {
  color: "#032E68",
  backgroundColor: "#CEECFF",
  "&:hover": {
    backgroundColor: "#BDDFF0",
  },
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: "18px",
  letterSpacing: "1.5px",
  borderRadius: 1.5,
};

const SHIFT_CHIP_TYPE_MAP: Record<ShiftChipType, ShiftChipData> = {
  [ShiftChipTypes.URGENT]: {
    type: ShiftChipTypes.URGENT,
    label: "Urgent",
    color: "warning",
    getTooltip: () =>
      "We significantly increase the pay for Urgent Shifts to quickly find a replacement.",
  },
  [ShiftChipTypes.WORKER_LEFT_EARLY_REPLACEMENT_WORKER]: {
    type: ShiftChipTypes.WORKER_LEFT_EARLY_REPLACEMENT_WORKER,
    label: "Replacement",
    color: "info",
    getTooltip: () =>
      "This shift is created by a worker left early request. We're looking for a replacement worker.",
  },
  [ShiftChipTypes.WORKER_LEFT_EARLY]: {
    type: ShiftChipTypes.WORKER_LEFT_EARLY,
    label: "Worker Left Early",
    color: "error",
    getTooltip: () => "The worker left early for this shift.",
  },
  [ShiftChipTypes.STILL_BOOKABLE]: {
    type: ShiftChipTypes.STILL_BOOKABLE,
    label: "Still Bookable",
    color: "success",
    getTooltip: (tooltipData) => {
      if (!tooltipData?.extendedBookabilityTo || !tooltipData.facilityTimeZone) {
        return "";
      }

      const extendedBookabilityToFacilityTz = utcToZonedTime(
        tooltipData.extendedBookabilityTo,
        tooltipData.facilityTimeZone
      );

      const timePart = formatTime(extendedBookabilityToFacilityTz);
      let fullTime = timePart;
      if (isToday(tooltipData.extendedBookabilityTo)) {
        fullTime += " today";
      } else if (isTomorrow(tooltipData.extendedBookabilityTo)) {
        fullTime += " tomorrow";
      } else {
        fullTime += ` on ${formatDate(tooltipData.extendedBookabilityTo)}`;
      }

      return `Booking is allowed until ${fullTime}.`;
    },
  },
  [ShiftChipTypes.ATEAM_EXCLUSIVE_BLOCK]: {
    type: ShiftChipTypes.ATEAM_EXCLUSIVE_BLOCK,
    label: "A-Team Exclusive",
    color: "info",
    getTooltip: () => "This shift block is bookable only by your A-Team.",
    customStyles: aTeamChipStyles,
  },
  [ShiftChipTypes.AWAITING_REQUESTS]: {
    type: ShiftChipTypes.AWAITING_REQUESTS,
    label: "Awaiting Requests",
    color: "default",
    getTooltip: () => "No requests yet — we're gathering qualified professionals for your review",
  },
  [ShiftChipTypes.PENDING_REQUESTS]: {
    type: ShiftChipTypes.PENDING_REQUESTS,
    label: "Pending Request(s)",
    color: "primary",
    getTooltip: () => "",
    customStyles: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: CHIP_COLORS.primary.backgroundColor,
        cursor: "pointer",
      },
    },
  },
  [ShiftChipTypes.INSTANT_BOOK]: {
    type: ShiftChipTypes.INSTANT_BOOK,
    label: "Instant Book",
    color: "success",
    getTooltip: () => "Qualified professionals can book this shift instantly",
  },
};

export function getShiftChipDataFromShiftChip(shiftChip: ShiftChip): ShiftChipData {
  const baseData = SHIFT_CHIP_TYPE_MAP[shiftChip.type];
  return {
    ...baseData,
    ...shiftChip,
    label: shiftChip.label ?? baseData.label,
  };
}

const ShiftChipTypeOrder: Record<ShiftChipType, number> = {
  // Index 0 used to be MAGIC, but that feature has been removed.
  [ShiftChipTypes.URGENT]: 1,
  [ShiftChipTypes.STILL_BOOKABLE]: 2,
  [ShiftChipTypes.ATEAM_EXCLUSIVE_BLOCK]: 3,
  [ShiftChipTypes.WORKER_LEFT_EARLY_REPLACEMENT_WORKER]: 4,
  [ShiftChipTypes.WORKER_LEFT_EARLY]: 5,
  [ShiftChipTypes.AWAITING_REQUESTS]: 6,
  [ShiftChipTypes.PENDING_REQUESTS]: 7,
  [ShiftChipTypes.INSTANT_BOOK]: 8,
};

export function sortShiftChipData(shiftChipData: ShiftChipData[]): ShiftChipData[] {
  return shiftChipData.sort((a, b) => ShiftChipTypeOrder[a.type] - ShiftChipTypeOrder[b.type]);
}

export function formatTimeline(shiftStart: Date, shiftEnd: Date, timeZone: string): string {
  // use browser local timezone to convert datetimes to UTC
  const utcShiftStart = zonedTimeToUtc(
    shiftStart,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const utcShiftEnd = zonedTimeToUtc(shiftEnd, Intl.DateTimeFormat().resolvedOptions().timeZone);

  const shiftStartText = formatDateTime(utcShiftStart, { timeZone });
  const shiftEndText = formatDateTime(utcShiftEnd, { timeZone });
  const duration = formatDuration(intervalToDuration({ start: shiftStart, end: shiftEnd }));
  return `${shiftStartText} - ${shiftEndText} (${duration})`;
}

export const shouldShowStillBookableChip = (
  shift: Pick<
    Shift,
    "deleted" | "bookable" | "extendBookabilityTo" | "agentId" | "urgency" | "start" | "end"
  >
): shift is Shift & { extendBookabilityTo: string } => {
  if (shift.deleted ?? (isDefined(shift.bookable) && !shift.bookable)) {
    return false;
  }

  if (!shift.extendBookabilityTo) {
    return false;
  }

  if (
    isDefined(shift.agentId) ||
    shift.urgency === UrgencyType.NCNS ||
    shift.urgency === UrgencyType.LATE_CANCELLATION
  ) {
    return false;
  }

  const shiftStart = parseISO(shift.start);
  const extendedBookabilityTo = parseISO(shift.extendBookabilityTo);

  const now = new Date();
  return isBefore(shiftStart, now) && isAfter(extendedBookabilityTo, now);
};

export function shouldShowUrgentChip(parameters: {
  shift: Pick<Shift, "end" | "deleted" | "urgency">;
}): boolean {
  const { shift } = parameters;
  // don't show urgent chip if shift has ended
  if (new Date(shift.end) < new Date()) {
    return false;
  }

  // don't show urgent chip if shift is cancelled
  if (shift.deleted) {
    return false;
  }

  return shift.urgency === UrgencyType.NCNS || shift.urgency === UrgencyType.LATE_CANCELLATION;
}

export function shouldShowWorkerLeftEarlyRequestShiftChip(shift: Shift): boolean {
  return isDefined(shift.replacedShiftId);
}

export function shouldShowWorkerLeftEarlyShiftChip(shift: Shift): boolean {
  return isDefined(shift.workerLeftEarlyRequest?.id);
}

export function isShiftNoLongerBookable(
  shift: Pick<Shift, "extendBookabilityTo" | "start">
): boolean {
  const bookabilityEndDate = parseISO(shift.extendBookabilityTo ?? shift.start);
  return isPast(bookabilityEndDate);
}
